import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  TablePagination,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Card,
  Link,
  Popover,
  useTheme
} from '@mui/material';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import CustomPopover, {usePopover} from 'src/components/custom-popover';
import {
  Edit,
  Delete,
  Add,
  Search,
  Clear,
  FilterList,
  FilterListOff
} from '@mui/icons-material';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import {
  getAllPodsUsers,
  getAllUserGroupsByApplication,
  getApplicationPods
} from '@shared/services/service-handlers/PrivateService';
import {useSnackbar} from 'notistack';
import EncryptUtils from 'src/utils/EncryptUtils';
import {EditPodAccessDialog} from './EditPodAccessDialog';

// ── Types ──
interface PodAssignment {
  application_name: string;
  groupnames: string;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  created_at: string;
  pods: PodAssignment[];
  totalgroups: number;
  totalpods: number;
  user_application_name: string; // For backward compatibility with existing code that expects this field
}

interface Pod {
  id: number;
  name: string;
}

interface MasterPod {
  application_name: string;
  groups: Array<{groupid: string; groupName: string}>;
}

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  pagination?: Pagination;
}

type Pagination = {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
};

const MENU_PROPS = {
  PaperProps: {style: {maxHeight: 280, width: 280}}
};

// ── Row Actions ──
const RowActions: React.FC<{
  onEdit: () => void;
  onDelete: () => void;
}> = ({onEdit, onDelete}) => {
  const popover = usePopover();

  return (
    <>
      <IconButton
        color={popover.open ? 'inherit' : 'default'}
        onClick={popover.onOpen}>
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{width: 160}}>
        <MenuItem
          onClick={() => {
            onEdit();
            popover.onClose();
          }}>
          <Iconify icon="solar:pen-bold" />
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            onDelete();
            popover.onClose();
          }}
          sx={{color: 'error.main'}}>
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  );
};

// ── Pod Details Popover Cell ──
const PodDetailsCell: React.FC<{user: User}> = ({user}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Fix: parse the comma-separated string into an array of pod objects
  const pods: Array<{application_name: string; groupnames: string}> = useMemo(() => {
    if (!user.user_application_name) return [];

    // If it's already an array, use it directly
    if (Array.isArray(user.user_application_name))
      return user.user_application_name;

    // If it's a comma-separated string, split and map to objects
    return user.user_application_name
      .split(',')
      .map((name: string) => name.trim())
      .filter(Boolean)
      .map((name: string) => ({
        application_name: name,
        groupnames: '' // default empty if groupnames not available per pod
      }));
  }, [user.user_application_name]);

  if (pods.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        No pods assigned
      </Typography>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap>
        {pods.slice(0, 2).map((pod, i) => (
          <Label key={i} variant="soft" color="success" sx={{maxWidth: 160}}>
            {pod.application_name}
            {pod.groupnames &&  
              ` (${pod.groupnames.split(',').filter((g: string) => g.trim()).length})`}
          </Label>
        ))}
        {pods.length > 2 && (
          <Label variant="soft" color="default">
            +{pods.length - 2}
          </Label>
        )}
        <Link
          component="button"
          variant="caption"
          underline="always"
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            setAnchorEl(e.currentTarget)
          }
          sx={{
            color: theme.palette.primary.main,
            cursor: 'pointer',
            fontWeight: 600,
            ml: 0.5
          }}>
          View Details
        </Link>
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: 360,
              maxHeight: 400,
              borderRadius: 1.5,
              boxShadow: theme.shadows[8]
            }
          }
        }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{mb: 1.5}}>
          <Typography variant="subtitle2" fontWeight={700}>
            Pod & Group Details
          </Typography>
          <Stack direction="row" spacing={1}>
            <Label variant="soft" color="info">
              {pods.length} pods
            </Label>
          </Stack>
        </Stack>

        <Divider sx={{mb: 1.5}} />

        <Box sx={{maxHeight: 280, overflowY: 'auto'}}>
          {pods.map((pod, index) => (
            <Box
              key={index}
              sx={{
                mb: 1.5,
                pb: 1.5,
                borderBottom:
                  index < pods.length - 1
                    ? `1px dashed ${theme.palette.divider}`
                    : 'none'
              }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{mb: 0.75}}>
                <Iconify
                  icon="solar:widget-bold-duotone"
                  width={18}
                  sx={{color: theme.palette.success.main}}
                />
                <Typography variant="subtitle2">
                  {pod.application_name}
                </Typography>
              </Stack>

              {pod.groupnames && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{pl: 3.5}}>
                  {pod.groupnames
                    .split(',')
                    .filter((g: string) => g.trim())
                    .map((group, gIndex) => (
                      <Label
                        key={gIndex}
                        variant="soft"
                        color="default"
                        sx={{mb: 0.5}}>
                        {group.trim()}
                      </Label>
                    ))}
                </Stack>
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
const PodDetailsCellGroups: React.FC<{user: User}> = ({user}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const pods = user.pods || [];
  if (pods.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        No pods assigned
      </Typography>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        {pods.slice(0, 2).map((pod, i) => (
          <Label key={i} variant="soft" color="success" sx={{maxWidth: 160}}>
            {pod.application_name} ({pod.groupnames.split(',').filter(g => g.trim()).length})
          </Label>
        ))}
        {pods.length > 2 && (
          <Label variant="soft" color="default">
            +{pods.length - 2}
          </Label>
        )}
        <Link
          component="button"
          variant="caption"
          underline="always"
          onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
          sx={{color: theme.palette.primary.main, cursor: 'pointer', fontWeight: 600, ml: 0.5}}>
          View Details
        </Link>
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        slotProps={{
          paper: {
            sx: {p: 2, width: 360, maxHeight: 400, borderRadius: 1.5, boxShadow: theme.shadows[8]}
          }
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1.5}}>
          <Typography variant="subtitle2" fontWeight={700}>
            Pod & Group Details
          </Typography>
          <Stack direction="row" spacing={1}>
            <Label variant="soft" color="info">{user.totalpods} pods</Label>
            <Label variant="soft" color="warning">{user.totalgroups} groups</Label>
          </Stack>
        </Stack>

        <Divider sx={{mb: 1.5}} />

        <Box sx={{maxHeight: 280, overflowY: 'auto'}}>
          {pods.map((pod, index) => (
            <Box
              key={index}
              sx={{
                mb: 1.5,
                pb: 1.5,
                borderBottom: index < pods.length - 1 ? `1px dashed ${theme.palette.divider}` : 'none'
              }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.75}}>
                <Iconify icon="solar:widget-bold-duotone" width={18} sx={{color: theme.palette.success.main}} />
                <Typography variant="subtitle2">{pod.application_name}</Typography>
              </Stack>

              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{pl: 3.5}}>
                {pod.groupnames
                  .split(',')
                  .filter((g) => g.trim())
                  .map((group, gIndex) => (
                    <Label key={gIndex} variant="soft" color="default" sx={{mb: 0.5}}>
                      {group.trim()}
                    </Label>
                  ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

// ── Main Component ──
const UsersTable: React.FC = () => {
  const theme = useTheme();
  const {enqueueSnackbar} = useSnackbar();

  const [users, setUsers] = useState<User[]>([]);
  const [allPods, setAllPods] = useState<Pod[]>([]);
  const [masterPods, setMasterPods] = useState<MasterPod[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 25
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState<string[]>(
    []
  );
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const [modalState, setModalState] = useState({
    user: false,
    pod: false,
    delete: false
  });
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // ── Dependent groups: only show groups belonging to selected pods ──
  const filteredGroups = useMemo(() => {
    if (selectedApplications.length === 0) {
      // No pod selected → show all groups from all pods
      const allGroups = new Set<string>();
      masterPods.forEach(mp =>
        (mp.groups || []).forEach(g => {
          if (g.groupName) allGroups.add(g.groupName);
        })
      );
      return Array.from(allGroups).sort();
    }
    // Only groups from selected pods
    const groups = new Set<string>();
    masterPods
      .filter(mp => selectedApplications.includes(mp.application_name))
      .forEach(mp =>
        (mp.groups || []).forEach(g => {
          if (g.groupName) groups.add(g.groupName);
        })
      );
    return Array.from(groups).sort();
  }, [masterPods, selectedApplications]);

  // When selected applications change, remove groups that no longer apply
  useEffect(() => {
    if (selectedGroups.length > 0) {
      const valid = selectedGroups.filter(g => filteredGroups.includes(g));
      if (valid.length !== selectedGroups.length) {
        setSelectedGroups(valid);
      }
    }
  }, [filteredGroups]);

  // ── Debounce ──
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 600);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // ── Fetch users ──
  const fetchUsers = useCallback(
    (
      currentPage: number,
      pageSize: number,
      search: string,
      applications: string[],
      groups: string[]
    ) => {
      setIsLoading(true);
      const payload: any = {
        currentPage,
        pageSize,
        search,
        applications,
        groups
      };
      const tk = EncryptUtils.encryptedData(payload);
      getAllPodsUsers?.({tk})
        .then((res: ApiResponse<User[]>) => {
          if (res?.success && Array.isArray(res?.data)) {
            setUsers(res.data);
            if (res.pagination) setPagination(res.pagination);
          } else {
            setUsers([]);
          }
        })
        .catch((err: any) => console.error('Users API Error:', err))
        .finally(() => setIsLoading(false));
    },
    []
  );

  useEffect(() => {
    if (!isEditing) {
      fetchUsers(
        page,
        rowsPerPage,
        debouncedSearchTerm,
        selectedApplications,
        selectedGroups
      );
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearchTerm,
    selectedApplications,
    selectedGroups,
    isEditing,
    fetchUsers
  ]);

  // ── Fetch filter data (pods + groups-per-pod) ──
  const fetchFilterData = useCallback(async () => {
    try {
      const [podsRes, masterRes] = await Promise.all([
        getApplicationPods?.(),
        getAllUserGroupsByApplication?.()
      ]);
      if (podsRes?.success && Array.isArray(podsRes?.data))
        setAllPods(podsRes.data);
      if (masterRes?.success && Array.isArray(masterRes?.data))
        setMasterPods(masterRes.data);
    } catch (err) {
      console.error('Filter data error:', err);
    }
  }, []);

  useEffect(() => {
    if (showFilters && allPods.length === 0) fetchFilterData();
  }, [showFilters, allPods.length, fetchFilterData]);

  // ── Handlers ──
  const handleOpenUserModal = async (user: User | null = null) => {
    setCurrentUser(user);
    setIsEditing(!!user);
    setModalState(prev => ({...prev, user: true}));
  };

  const handleConfirmDelete = async () => {
    if (currentUser) {
      await fetchUsers(
        page,
        rowsPerPage,
        debouncedSearchTerm,
        selectedApplications,
        selectedGroups
      );
      setModalState(prev => ({...prev, delete: false}));
      setCurrentUser(null);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSelectedApplications([]);
    setSelectedGroups([]);
  };

  const handleApplicationChange = (event: any) => {
    const value = event.target.value;
    setSelectedApplications(
      typeof value === 'string' ? value.split(',') : value
    );
  };

  const handleGroupChange = (event: any) => {
    const value = event.target.value;
    setSelectedGroups(typeof value === 'string' ? value.split(',') : value);
  };

  const isSearching = searchTerm !== debouncedSearchTerm || isLoading;
  const hasActiveFilters =
    selectedApplications.length > 0 || selectedGroups.length > 0;
  const filtersCount = selectedApplications.length + selectedGroups.length;

  return (
    <Card>
      {/* ── Toolbar ── */}
      <Stack
        spacing={2}
        direction={{xs: 'column', sm: 'row'}}
        alignItems={{xs: 'flex-end', sm: 'center'}}
        sx={{px: 2.5, py: 2}}>
        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name or email..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{color: 'text.disabled'}} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {isSearching && (
                    <CircularProgress size={18} thickness={5} color="primary" />
                  )}
                  {searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setDebouncedSearchTerm('');
                      }}>
                      <Iconify icon="eva:close-fill" />
                    </IconButton>
                  )}
                </Stack>
              </InputAdornment>
            )
          }}
        />

        <Button
          variant={showFilters ? 'contained' : 'outlined'}
          color={showFilters ? 'primary' : 'inherit'}
          startIcon={<Iconify icon="ic:round-filter-list" />}
          onClick={() => setShowFilters(prev => !prev)}
          sx={{minWidth: 100, flexShrink: 0}}>
          Filters
          {filtersCount > 0 && (
            <Label
              variant="filled"
              color="primary"
              sx={{
                ml: 1,
                height: 20,
                minWidth: 20,
                borderRadius: '50%',
                fontSize: '0.7rem'
              }}>
              {filtersCount}
            </Label>
          )}
        </Button>
      </Stack>

      {/* ── Filters Panel ── */}
      <Collapse in={showFilters}>
        <Stack spacing={2} sx={{px: 2.5, pb: 2}}>
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
            {/* Applications (Pods) */}
            <FormControl fullWidth size="small">
              <InputLabel>Applications (Pods)</InputLabel>
              <Select
                multiple
                value={selectedApplications}
                onChange={handleApplicationChange}
                input={<OutlinedInput label="Applications (Pods)" />}
                renderValue={selected => (
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                    {selected.map(v => (
                      <Chip
                        key={v}
                        label={v}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MENU_PROPS}>
                {allPods.map(pod => (
                  <MenuItem key={pod.id} value={pod.name}>
                    <Checkbox
                      checked={selectedApplications.indexOf(pod.name) > -1}
                      size="small"
                    />
                    <ListItemText primary={pod.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Groups — dependent on selected pods */}
            <FormControl
              fullWidth
              size="small"
              disabled={filteredGroups.length === 0}>
              <InputLabel>
                Groups
                {selectedApplications.length > 0 && filteredGroups.length > 0
                  ? ` (${filteredGroups.length} available)`
                  : ''}
              </InputLabel>
              <Select
                multiple
                value={selectedGroups}
                onChange={handleGroupChange}
                input={
                  <OutlinedInput
                    label={
                      selectedApplications.length > 0 &&
                      filteredGroups.length > 0
                        ? `Groups (${filteredGroups.length} available)`
                        : 'Groups'
                    }
                  />
                }
                renderValue={selected => (
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                    {selected.map(v => (
                      <Chip
                        key={v}
                        label={v}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MENU_PROPS}>
                {filteredGroups.map(group => (
                  <MenuItem key={group} value={group}>
                    <Checkbox
                      checked={selectedGroups.indexOf(group) > -1}
                      size="small"
                    />
                    <ListItemText primary={group} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {hasActiveFilters && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between">
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selectedApplications.map(app => (
                  <Chip
                    key={app}
                    label={app}
                    size="small"
                    color="primary"
                    onDelete={() =>
                      setSelectedApplications(prev =>
                        prev.filter(a => a !== app)
                      )
                    }
                  />
                ))}
                {selectedGroups.map(group => (
                  <Chip
                    key={group}
                    label={group}
                    size="small"
                    color="info"
                    onDelete={() =>
                      setSelectedGroups(prev => prev.filter(g => g !== group))
                    }
                  />
                ))}
              </Stack>
              <Button
                size="small"
                color="error"
                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={handleClearFilters}>
                Clear all
              </Button>
            </Stack>
          )}
        </Stack>
        <Divider />
      </Collapse>

      {/* ── Table ── */}
        <TableContainer sx={{minWidth: 720}}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{pl: 3}}>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Pod (Groups)</TableCell>
                {/* <TableCell align="right" sx={{pr: 3, width: 80}}>Actions</TableCell> */}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{py: 6}}>
                    <CircularProgress size={36} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{py: 6}}>
                    <Stack alignItems="center" spacing={1}>
                      <Iconify
                        icon="solar:users-group-rounded-bold-duotone"
                        width={48}
                        sx={{color: 'text.disabled'}}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{pl: 3}}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {user.full_name}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {user.email}
                      </Typography>
                    </TableCell>

                    {/* <TableCell>
                      <PodDetailsCell user={user} />
                    </TableCell> */}
                     <TableCell>
                      <PodDetailsCellGroups user={user} />
                    </TableCell>

                  {/* <TableCell align="right" sx={{pr: 3}}>
                    
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => handleOpenUserModal(user)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => {
                          setCurrentUser(user);
                          setIsDeleting(true);
                          setModalState(p => ({...p, delete: true}));
                        }}>
                        Delete
                      </Button>
                    </Stack>
                  </TableCell> */}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{borderTop: t => `solid 1px ${t.palette.divider}`}}
      />

      {/* ── Dialogs ── */}
      <DeleteConfirmDialog
        open={modalState.delete}
        userInfo={currentUser}
        onClose={() => setModalState(p => ({...p, delete: false}))}
        onConfirm={handleConfirmDelete}
      />
      <EditPodAccessDialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        userId={currentUser?.id}
      />
    </Card>
  );
};

export default UsersTable;
